import handleSubMenuClick from '../function/HandleSubMenuClick';
import { useNavigation } from '../function/navigation';
import { UnitConversion } from '../inputs/UnitConversion';

export function LoadCheck({ inputs, options, result, clickTriggeredRef }) {
  const { dispatch } = useNavigation();
  const dependencies = ['belt_guide', 'chain_material', 'centerline_speed'];
  const isProduct = inputs.product_tbh?.length > 0;
  const unitSystem = inputs.unit_system;
  const fields = loadFields(isProduct);

  const inputMissing = dependencies.some(
    (dep) =>
      ['', null, undefined, 'select an option'].includes(inputs[dep]) &&
      [null, undefined].includes(result?.[dep])
  );

  const warningMissing = () => {
    if (!inputMissing) return;
    for (const element of dependencies) {
      if (
        ['', null, undefined, 'select an option'].includes(inputs[element]) &&
        [null, undefined].includes(result?.[element])
      ) {
        const option = options.find((opt) => opt.field === element);
        return `${option?.name ?? element.replace(/_/g, ' ').trim()} missing`;
      }
    }
  };

  const numeric = (v) =>
    typeof v === 'object' && v !== null
      ? Number(Object.values(v)[0])
      : Number(String(v).replace('%', ''));

  const renderLoadField = fields.map(([field1, field2, baseUnit], index) => {
    const raw1 = numeric(result[field1]);
    const raw2 = numeric(result[field2]);

    const isPercent = baseUnit === '%';

    const { displayedValue: v1, displayedUnit } = isPercent
      ? { displayedValue: raw1, displayedUnit: '%' }
      : UnitConversion(raw1, unitSystem, baseUnit);

    const { displayedValue: v2 } = isPercent
      ? { displayedValue: raw2 }
      : UnitConversion(raw2, unitSystem, baseUnit);

    const isValid = Number.isFinite(raw1) && Number.isFinite(raw2) && raw2 > 0;

    const percent = isValid
      ? Math.min(100, Math.max(0, (raw1 / raw2) * 100))
      : 0;

    const label = isPercent
      ? `${Math.round(v1)} / ${Math.round(v2)} %`
      : `${Math.round(v1)} / ${Math.round(v2)} ${displayedUnit}`;

    return (
      <div
        key={index}
        className={`result-product ${raw1 > raw2 ? 'not-checked' : ''}`}
      >
        <div className="result-product-name">
          {options.find((option) => option.field === field1)?.name}
        </div>
        <div className="result-product-value">
          <div
            className="result-product-progress"
            role="progressbar"
            aria-valuenow={Math.round(percent)}
            aria-valuemin={0}
            aria-valuemax={100}
            data-label={label}
          >
            <div
              className={`result-product-progress-fill ${
                raw1 > raw2 ? 'over-limit' : ''
              }`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>
    );
  });

  const setCurrentPage = (page) =>
    dispatch({ type: 'currentPage', payload: page });

  const handleItemMissingClick = () => {
    dispatch({ type: 'phoneMenu', payload: 'params' });
    dispatch({ type: 'mainPage', payload: 'machine' });
    handleSubMenuClick('Flow', setCurrentPage, clickTriggeredRef);
  };

  return inputMissing ? (
    <>
      <div className="result-name item-missing">Load Check</div>
      <div
        className="result-value item-missing"
        onClick={handleItemMissingClick}
      >
        {warningMissing()}
      </div>
    </>
  ) : (
    <>
      <div
        className={`result-name ${
          isLoadCheckPassed(isProduct, result) ? 'checked' : 'not-checked'
        }`}
      >
        Load Check
      </div>
      <div className="result-product-container">{renderLoadField}</div>
    </>
  );
}

const extractNumericValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    const firstKey = Object.keys(value)[0];
    value = value[firstKey];
  }
  if (typeof value === 'string') {
    value = parseFloat(value.replace('%', ''));
  }
  return Number(value) || 0;
};

const loadFields = (isProduct) =>
  isProduct
    ? [
        ['product_load_line', 'max_allowed_load_line', 'kg/m'],
        ['total_product_weight', 'max_total_load', 'kg'],
        ['filling_rate_percent', 'critical_filling_rate_percent', '%'],
        ['torques', 'torque_rating', 'Nm'],
      ]
    : [['torques', 'torque_rating', 'Nm']];

export const isLoadCheckPassed = (isProduct, result) => {
  return loadFields(isProduct).every(([field1, field2, _]) => {
    return (
      extractNumericValue(result[field2]) > extractNumericValue(result[field1])
    );
  });
};

export const shouldShowLoadCheck = (isProduct) => {
  return loadFields(isProduct).length > 0;
};
